import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import PageWithHeader from '../common/PageWithHeader';
import LoadingCover from '../common/LoadingCover';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useGetPEARByIdQuery,
  useUpdatePEARReviewMutation,
  useApprovePEARReviewMutation,
  useManagerEditPEARReviewMutation,
  PEARReview,
} from '../../redux/pearReviewApi';
import { useGetUserByIdQuery } from '../../redux/vmsApi';

// Grade colors mapping
const gradeColors: Record<string, string> = {
  'A+': '#033e79',
  'A': '#17A2B8',
  'A-': '#17A2B8',
  'B+': '#32CD32',
  'B': '#32CD32',
  'C+': '#FFC107',
  'C': '#FFC107',
  'C-': '#FFC107',
  'D': '#DC3545',
  'E': '#DC3545',
  'F': '#DC3545',
};

// Rating scale info for PEAR
const RATING_SCALE = [
  { grade: 'A', min: 110, max: 999, description: 'Exceeds expectations', color: '#4caf50' },
  { grade: 'B', min: 95, max: 109.99, description: 'Solid performance', color: '#8bc34a' },
  { grade: 'C', min: 80, max: 94.99, description: 'Partially met expectations', color: '#ff9800' },
  { grade: 'D', min: 0, max: 79.99, description: 'Improvement needed', color: '#f44336' },
];

// Helper to get score color
const getScoreColor = (score?: number): string => {
  if (score === undefined || score === null) return '#9e9e9e';
  if (score >= 110) return '#033e79';
  if (score >= 100) return '#17A2B8';
  if (score >= 85) return '#32CD32';
  if (score >= 70) return '#FFC107';
  return '#DC3545';
};

// Available rating options for manager rating
const RATING_OPTIONS = ['A+', 'A', 'A-', 'B+', 'B', 'C+', 'C', 'C-', 'D', 'E', 'F'];

// OKR Rating options (1-5)
const OKR_RATING_OPTIONS = [1, 2, 3, 4, 5];

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultExpanded = false }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Paper sx={{ mb: 2 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          backgroundColor: '#f5f5f5',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

// Score Item Display
interface ScoreItemDisplayProps {
  items: Array<{ criterion: string; score: number; comments?: string }>;
  maxScore: number;
}

const ScoreItemDisplay: React.FC<ScoreItemDisplayProps> = ({ items, maxScore }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Criterion</TableCell>
          <TableCell align="center" width={100}>Score</TableCell>
          <TableCell>Comments</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items?.map((item, idx) => (
          <TableRow key={idx}>
            <TableCell>{item.criterion}</TableCell>
            <TableCell align="center">
              <Chip
                label={`${item.score}/${maxScore}`}
                size="small"
                sx={{ bgcolor: item.score >= maxScore * 0.8 ? '#4caf50' : item.score >= maxScore * 0.6 ? '#ff9800' : '#f44336', color: 'white' }}
              />
            </TableCell>
            <TableCell>{item.comments || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Key Factor Display (Editable for Manager)
interface KeyFactorDisplayProps {
  categories: Array<{
    category: string;
    subItems: Array<{ description: string; selfRating: string; managerRating: string }>;
  }>;
  onManagerRatingChange?: (categoryIndex: number, itemIndex: number, rating: string) => void;
  isEditable?: boolean;
}

const KeyFactorDisplay: React.FC<KeyFactorDisplayProps> = ({ categories, onManagerRatingChange, isEditable = false }) => (
  <Box>
    {categories?.map((cat, catIdx) => (
      <Box key={catIdx} sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{cat.category}</Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Description</TableCell>
                <TableCell align="center" width={100}>Self Rating</TableCell>
                <TableCell align="center" width={120}>Manager Rating</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cat.subItems?.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell align="center">
                    <Chip label={item.selfRating || '-'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    {isEditable && onManagerRatingChange ? (
                      <FormControl size="small" sx={{ minWidth: 80 }}>
                        <Select
                          value={item.managerRating || ''}
                          onChange={(e) => onManagerRatingChange(catIdx, idx, e.target.value as string)}
                          displayEmpty
                          sx={{ 
                            '& .MuiSelect-select': { py: 0.5 },
                            bgcolor: item.managerRating ? gradeColors[item.managerRating] + '20' : 'transparent',
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          {RATING_OPTIONS.map(opt => (
                            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Chip
                        label={item.managerRating || '-'}
                        size="small"
                        sx={{ bgcolor: gradeColors[item.managerRating] || '#9e9e9e', color: 'white' }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    ))}
  </Box>
);

// OKR Display (Editable for Manager)
interface OKRDisplayProps {
  objective?: string;
  objectiveChallenge?: number;
  keyResults: Array<{
    description: string;
    selfRating: number;
    managerPerformanceRating: number;
    managerAlignmentRating: number;
    managerComment: string;
  }>;
  onManagerRatingChange?: (index: number, field: 'managerPerformanceRating' | 'managerAlignmentRating' | 'managerComment', value: number | string) => void;
  isEditable?: boolean;
}

const OKRDisplay: React.FC<OKRDisplayProps> = ({ objective, objectiveChallenge, keyResults, onManagerRatingChange, isEditable = false }) => (
  <Box>
    <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
      <Typography variant="subtitle2" color="text.secondary">Objective</Typography>
      <Typography variant="body1" fontWeight="bold">{objective || 'Not set'}</Typography>
      {objectiveChallenge && (
        <Chip label={`Challenge Level: ${objectiveChallenge}/5`} size="small" sx={{ mt: 1 }} />
      )}
    </Paper>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Key Result</TableCell>
            <TableCell align="center" width={80}>Self</TableCell>
            <TableCell align="center" width={100}>Performance</TableCell>
            <TableCell align="center" width={100}>Alignment</TableCell>
            <TableCell width={200}>Comment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keyResults?.map((kr, idx) => (
            <TableRow key={idx}>
              <TableCell>{kr.description}</TableCell>
              <TableCell align="center">{kr.selfRating || '-'}</TableCell>
              <TableCell align="center">
                {isEditable && onManagerRatingChange ? (
                  <FormControl size="small" sx={{ minWidth: 60 }}>
                    <Select
                      value={kr.managerPerformanceRating || ''}
                      onChange={(e) => onManagerRatingChange(idx, 'managerPerformanceRating', e.target.value as number)}
                      displayEmpty
                      sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                    >
                      <MenuItem value="">-</MenuItem>
                      {OKR_RATING_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  kr.managerPerformanceRating || '-'
                )}
              </TableCell>
              <TableCell align="center">
                {isEditable && onManagerRatingChange ? (
                  <FormControl size="small" sx={{ minWidth: 60 }}>
                    <Select
                      value={kr.managerAlignmentRating || ''}
                      onChange={(e) => onManagerRatingChange(idx, 'managerAlignmentRating', e.target.value as number)}
                      displayEmpty
                      sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                    >
                      <MenuItem value="">-</MenuItem>
                      {OKR_RATING_OPTIONS.map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  kr.managerAlignmentRating || '-'
                )}
              </TableCell>
              <TableCell>
                {isEditable && onManagerRatingChange ? (
                  <TextField
                    size="small"
                    fullWidth
                    value={kr.managerComment || ''}
                    onChange={(e) => onManagerRatingChange(idx, 'managerComment', e.target.value)}
                    placeholder="Comment..."
                    sx={{ '& .MuiInputBase-input': { py: 0.5 } }}
                  />
                ) : (
                  kr.managerComment || '-'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

// Core Values Display (Editable for Manager)
interface CoreValuesDisplayProps {
  values: Array<{
    value: string;
    acronym: string;
    description: string;
    selfRating: string;
    managerRating: string;
    group: 'ACT' | 'REF';
  }>;
  onManagerRatingChange?: (valueIndex: number, rating: string) => void;
  isEditable?: boolean;
}

const CoreValuesDisplay: React.FC<CoreValuesDisplayProps> = ({ values, onManagerRatingChange, isEditable = false }) => {
  const actValues = values?.filter(v => v.group === 'ACT') || [];
  const refValues = values?.filter(v => v.group === 'REF') || [];

  // Function to find original index in full values array
  const getOriginalIndex = (group: 'ACT' | 'REF', idx: number): number => {
    const filteredValues = values?.filter(v => v.group === group) || [];
    const targetValue = filteredValues[idx];
    return values?.findIndex(v => v.acronym === targetValue.acronym && v.group === targetValue.group) ?? -1;
  };

  const renderValueTable = (groupValues: typeof actValues, group: 'ACT' | 'REF', title: string) => (
    <>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Value</TableCell>
              <TableCell align="center" width={80}>Self</TableCell>
              <TableCell align="center" width={120}>Manager</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupValues.map((v, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  <strong>{v.acronym}</strong> - {v.value}
                </TableCell>
                <TableCell align="center">
                  <Chip label={v.selfRating || '-'} size="small" variant="outlined" />
                </TableCell>
                <TableCell align="center">
                  {isEditable && onManagerRatingChange ? (
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={v.managerRating || ''}
                        onChange={(e) => onManagerRatingChange(getOriginalIndex(group, idx), e.target.value as string)}
                        displayEmpty
                        sx={{ 
                          '& .MuiSelect-select': { py: 0.5 },
                          bgcolor: v.managerRating ? gradeColors[v.managerRating] + '20' : 'transparent',
                        }}
                      >
                        <MenuItem value="">-</MenuItem>
                        {RATING_OPTIONS.map(opt => (
                          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <Chip
                      label={v.managerRating || '-'}
                      size="small"
                      sx={{ bgcolor: gradeColors[v.managerRating] || '#9e9e9e', color: 'white' }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        {renderValueTable(actValues, 'ACT', 'ACT Values')}
      </Grid>
      <Grid item xs={12} md={6}>
        {renderValueTable(refValues, 'REF', 'REF Values')}
      </Grid>
    </Grid>
  );
};

// Main Component
const PEARManagerReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: pearData, isLoading, error, refetch } = useGetPEARByIdQuery({ id: id || '' }, { skip: !id });
  const { data: userData } = useGetUserByIdQuery({ id: pearData?.employeeId || '' }, { skip: !pearData?.employeeId });

  const [updatePEAR, { isLoading: isUpdating }] = useUpdatePEARReviewMutation();
  const [approvePEAR, { isLoading: isApproving }] = useApprovePEARReviewMutation();
  const [managerEditPEAR, { isLoading: isManagerEditing }] = useManagerEditPEARReviewMutation();

  const [managerComments, setManagerComments] = useState('');
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Editable manager rating fields
  const [performanceKeyFactors, setPerformanceKeyFactors] = useState<Array<{
    category: string;
    subItems: Array<{ description: string; selfRating: string; managerRating: string }>;
  }>>([]);
  const [coreValues, setCoreValues] = useState<Array<{
    value: string;
    acronym: string;
    description: string;
    selfRating: string;
    managerRating: string;
    group: 'ACT' | 'REF';
  }>>([]);
  const [okrKeyResults, setOkrKeyResults] = useState<Array<{
    description: string;
    selfRating: number;
    managerPerformanceRating: number;
    managerAlignmentRating: number;
    managerComment: string;
  }>>([]);

  // Load PEAR data into state
  useEffect(() => {
    if (pearData?.overallManagerComments) {
      setManagerComments(pearData.overallManagerComments);
    }
    if (pearData?.performanceKeyFactors) {
      setPerformanceKeyFactors(JSON.parse(JSON.stringify(pearData.performanceKeyFactors)));
    }
    if (pearData?.coreValues) {
      setCoreValues(JSON.parse(JSON.stringify(pearData.coreValues)));
    }
    if (pearData?.okrKeyResults) {
      setOkrKeyResults(JSON.parse(JSON.stringify(pearData.okrKeyResults)));
    }
  }, [pearData]);

  // Handler for performance key factors manager rating change
  const handleKeyFactorRatingChange = (categoryIndex: number, itemIndex: number, rating: string) => {
    setPerformanceKeyFactors(prev => {
      const updated = [...prev];
      if (updated[categoryIndex] && updated[categoryIndex].subItems[itemIndex]) {
        updated[categoryIndex] = {
          ...updated[categoryIndex],
          subItems: [...updated[categoryIndex].subItems],
        };
        updated[categoryIndex].subItems[itemIndex] = {
          ...updated[categoryIndex].subItems[itemIndex],
          managerRating: rating,
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  // Handler for core values manager rating change
  const handleCoreValueRatingChange = (valueIndex: number, rating: string) => {
    setCoreValues(prev => {
      const updated = [...prev];
      if (updated[valueIndex]) {
        updated[valueIndex] = {
          ...updated[valueIndex],
          managerRating: rating,
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  // Handler for OKR manager rating change
  const handleOkrRatingChange = (index: number, field: 'managerPerformanceRating' | 'managerAlignmentRating' | 'managerComment', value: number | string) => {
    setOkrKeyResults(prev => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }
      return updated;
    });
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!id) return;

    try {
      await managerEditPEAR({
        id,
        body: {
          overallManagerComments: managerComments,
          performanceKeyFactors,
          coreValues,
          okrKeyResults,
        },
      }).unwrap();
      setHasChanges(false);
      alert('Changes saved successfully');
      refetch();
    } catch (error) {
      console.error('Error saving changes:', error);
      alert('Error saving changes');
    }
  };

  const handleApprove = async () => {
    if (!id) return;

    try {
      // Save any pending changes first (including manager ratings)
      if (hasChanges) {
        await managerEditPEAR({
          id,
          body: {
            overallManagerComments: managerComments,
            performanceKeyFactors,
            coreValues,
            okrKeyResults,
          },
        }).unwrap();
      }

      // Then approve
      await approvePEAR({ id }).unwrap();
      setShowApproveDialog(false);
      alert('PEAR approved successfully');
      navigate(-1);
    } catch (error) {
      console.error('Error approving PEAR:', error);
      alert('Error approving PEAR');
    }
  };

  const handleReject = async () => {
    if (!id || !managerComments.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      await updatePEAR({
        id,
        body: {
          status: 'rejected',
          overallManagerComments: managerComments,
        },
      }).unwrap();
      setShowRejectDialog(false);
      alert('PEAR rejected');
      navigate(-1);
    } catch (error) {
      console.error('Error rejecting PEAR:', error);
      alert('Error rejecting PEAR');
    }
  };

  if (isLoading) return <LoadingCover />;

  if (error || !pearData) {
    return (
      <PageWithHeader>
        <Box sx={{ maxWidth: 1400, mx: 'auto', p: 3 }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="error">PEAR Review not found</Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ mt: 2 }}
            >
              Go Back
            </Button>
          </Paper>
        </Box>
      </PageWithHeader>
    );
  }

  const employeeName = userData?.name || pearData.employeeName || 'Employee';
  const isApproved = pearData.status === 'approved';
  const ratingInfo = RATING_SCALE.find(r => (pearData.overallScore || 0) >= r.min && (pearData.overallScore || 0) <= r.max);

  return (
    <PageWithHeader>
      <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4">PEAR Review</Typography>
              <Typography variant="subtitle1" color="text.secondary">
                {employeeName} - {pearData.reviewPeriod}
              </Typography>
            </Box>
            <Chip
              label={pearData.status?.toUpperCase()}
              color={pearData.status === 'approved' ? 'success' : pearData.status === 'submitted' ? 'info' : 'default'}
            />
            {hasChanges && (
              <Chip label="Unsaved Changes" color="warning" variant="outlined" />
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveChanges}
              disabled={!hasChanges || isUpdating || isManagerEditing}
            >
              {isUpdating || isManagerEditing ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => setShowRejectDialog(true)}
              disabled={isApproved}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => setShowApproveDialog(true)}
              disabled={isApproved || isApproving}
            >
              Approve
            </Button>
          </Stack>
        </Box>

        {/* Employee Info Card */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Employee</Typography>
              <Typography fontWeight="bold">{employeeName}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Designation</Typography>
              <Typography fontWeight="bold">{pearData.designation || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Department</Typography>
              <Typography fontWeight="bold">{pearData.department || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">Period</Typography>
              <Typography fontWeight="bold">{pearData.reviewPeriod}</Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Approved Alert */}
        {isApproved && (
          <Alert severity="success" sx={{ mb: 3 }}>
            This PEAR has been approved. All fields are now read-only.
          </Alert>
        )}

        {/* Score Summary */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Score Breakdown</Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Functional Skills Total</Typography>
                  <Typography variant="body2" fontWeight="bold">{pearData.functionalSkillsTotal?.toFixed(0) || 0} / 200</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(((pearData.functionalSkillsTotal || 0) / 200) * 100, 100)}
                  sx={{ height: 8, borderRadius: 4 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="body2">Interpersonal Total</Typography>
                  <Typography variant="body2" fontWeight="bold">{pearData.interpersonalTotal?.toFixed(0) || 0} / 100</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(((pearData.interpersonalTotal || 0) / 100) * 100, 100)}
                  sx={{ height: 8, borderRadius: 4 }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                  <Typography variant="body2">Raw Total</Typography>
                  <Typography variant="body2" fontWeight="bold">{pearData.rawTotal?.toFixed(0) || 0} / 300</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(((pearData.rawTotal || 0) / 300) * 100, 100)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  textAlign: 'center',
                  p: 3,
                  borderRadius: 2,
                  backgroundColor: ratingInfo?.color || '#f5f5f5',
                  color: 'white',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                }}
              >
                <Typography variant="h2" sx={{ fontWeight: 'bold' }}>
                  {pearData.finalGrade || '-'}
                </Typography>
                <Typography variant="h4">
                  {pearData.overallScore?.toFixed(1) || 0}%
                </Typography>
                <Typography variant="body1" sx={{ mt: 1 }}>
                  {ratingInfo?.description || pearData.ratingDescription}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Part 1: Functional Skills */}
        <CollapsibleSection title="Part 1: Functional Skills">
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Quality of Work</Typography>
              <ScoreItemDisplay items={pearData.qualityOfWork || []} maxScore={10} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Work Habits</Typography>
              <ScoreItemDisplay items={pearData.workHabits || []} maxScore={10} />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Job Knowledge</Typography>
              <ScoreItemDisplay items={pearData.jobKnowledge || []} maxScore={10} />
            </Grid>
          </Grid>
        </CollapsibleSection>

        {/* Part 1: Interpersonal Skills */}
        <CollapsibleSection title="Part 1: Interpersonal Skills">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Interpersonal Skills</Typography>
              <ScoreItemDisplay items={pearData.interpersonalSkills || []} maxScore={10} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Leadership & Problem Solving</Typography>
              <ScoreItemDisplay items={pearData.leadershipProblemSolving || []} maxScore={10} />
            </Grid>
          </Grid>
        </CollapsibleSection>

        {/* Part 2: Performance Key Factors */}
        <CollapsibleSection title="Part 2: Performance Key Factors">
          <KeyFactorDisplay 
            categories={performanceKeyFactors.length > 0 ? performanceKeyFactors : (pearData.performanceKeyFactors || [])} 
            onManagerRatingChange={handleKeyFactorRatingChange}
            isEditable={!isApproved}
          />
          {pearData.performanceKeyFactorsManagerComments && (
            <Paper sx={{ p: 2, mt: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" color="text.secondary">Manager Comments</Typography>
              <Typography>{pearData.performanceKeyFactorsManagerComments}</Typography>
            </Paper>
          )}
        </CollapsibleSection>

        {/* Part 2: BSC Reference */}
        {pearData.bscScore !== undefined && (
          <CollapsibleSection title="Part 2: BSC Reference">
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">BSC Quarter</Typography>
                  <Typography fontWeight="bold">{pearData.bscQuarter || pearData.reviewPeriod}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">BSC Score</Typography>
                  <Typography variant="h5" fontWeight="bold" color={getScoreColor(pearData.bscScore)}>
                    {pearData.bscScore?.toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>
              {pearData.bscManagerComments && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">BSC Manager Comments</Typography>
                  <Typography>{pearData.bscManagerComments}</Typography>
                </Box>
              )}
            </Paper>
          </CollapsibleSection>
        )}

        {/* Part 2: OKRs */}
        {(pearData.okrObjective || (pearData.okrKeyResults && pearData.okrKeyResults.length > 0)) && (
          <CollapsibleSection title="Part 2: OKRs">
            <OKRDisplay
              objective={pearData.okrObjective}
              objectiveChallenge={pearData.okrObjectiveChallenge}
              keyResults={okrKeyResults.length > 0 ? okrKeyResults : (pearData.okrKeyResults || [])}
              onManagerRatingChange={handleOkrRatingChange}
              isEditable={!isApproved}
            />
          </CollapsibleSection>
        )}

        {/* Part 2: Core Values */}
        {pearData.coreValues && pearData.coreValues.length > 0 && (
          <CollapsibleSection title="Part 2: Core Values (ACT-REF)">
            <CoreValuesDisplay 
              values={coreValues.length > 0 ? coreValues : (pearData.coreValues || [])} 
              onManagerRatingChange={handleCoreValueRatingChange}
              isEditable={!isApproved}
            />
          </CollapsibleSection>
        )}

        {/* Part 2: Development Needs */}
        {pearData.developmentNeeds && pearData.developmentNeeds.length > 0 && (
          <CollapsibleSection title="Part 2: Development & Growth">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#fff3e0' }}>
                  <Typography variant="subtitle2" fontWeight="bold">Development Needs</Typography>
                  <ul>
                    {pearData.developmentNeeds.map((need, idx) => (
                      <li key={idx}><Typography variant="body2">{need}</Typography></li>
                    ))}
                  </ul>
                </Paper>
              </Grid>
              {pearData.managerDevelopmentSupport && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#e3f2fd' }}>
                    <Typography variant="subtitle2" fontWeight="bold">Manager Development Support</Typography>
                    <Typography>{pearData.managerDevelopmentSupport}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </CollapsibleSection>
        )}

        {/* Strengths */}
        {pearData.strengths && pearData.strengths.length > 0 && (
          <Paper sx={{ p: 3, mb: 3, backgroundColor: '#e8f5e9' }}>
            <Typography variant="h6" gutterBottom>Employee Strengths</Typography>
            <ul>
              {pearData.strengths.map((strength, idx) => (
                <li key={idx}><Typography>{strength}</Typography></li>
              ))}
            </ul>
          </Paper>
        )}

        {/* Manager Comments */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Manager Comments</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={managerComments}
            onChange={(e) => {
              setManagerComments(e.target.value);
              setHasChanges(true);
            }}
            placeholder="Add your overall comments about this PEAR submission..."
            disabled={isApproved}
          />
        </Paper>

        {/* Action Buttons (Bottom) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSaveChanges}
            disabled={!hasChanges || isUpdating || isManagerEditing}
          >
            Save Changes
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<CancelIcon />}
            onClick={() => setShowRejectDialog(true)}
            disabled={isApproved}
          >
            Reject
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={() => setShowApproveDialog(true)}
            disabled={isApproved || isApproving}
          >
            Approve
          </Button>
        </Box>

        {/* Approve Dialog */}
        <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Approve PEAR Review</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              You are about to approve the PEAR submission for <strong>{employeeName}</strong> for period <strong>{pearData.reviewPeriod}</strong>.
            </Alert>
            <Typography variant="body2" gutterBottom>
              Final Score: <strong>{pearData.overallScore?.toFixed(1)}%</strong> | 
              Grade: <strong>{pearData.finalGrade}</strong>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Manager Comments (Optional)"
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleApprove}
              disabled={isApproving}
            >
              {isApproving ? 'Approving...' : 'Approve'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reject PEAR Review</DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              You are about to reject the PEAR submission for <strong>{employeeName}</strong>. Please provide a reason.
            </Alert>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Reason for Rejection (Required)"
              value={managerComments}
              onChange={(e) => setManagerComments(e.target.value)}
              required
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={!managerComments.trim() || isUpdating}
            >
              {isUpdating ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PageWithHeader>
  );
};

export default PEARManagerReview;
